
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Client } from '@/types/client';
import { apiService } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

interface BasicInfoSectionProps {
  form: UseFormReturn<Client>;
  isEditing?: boolean;
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({ form, isEditing = false }) => {
  const [loadingCnpj, setLoadingCnpj] = useState(false);

  const handleFetchCnpjData = async (value: string) => {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length !== 14) return;
    setLoadingCnpj(true);
    try {
      const data = await apiService.fetchCompanyByCnpj(digits);
      if (data.company_tax_id != null) form.setValue('company_tax_id', data.company_tax_id);
      if (data.legal_name != null) form.setValue('legal_name', data.legal_name);
      if (data.trade_name != null) form.setValue('trade_name', data.trade_name);
      if (data.email != null) form.setValue('email', data.email);
      if (data.address_street != null) form.setValue('address_street', data.address_street);
      if (data.address_number != null) form.setValue('address_number', data.address_number);
      if (data.address_neighborhood != null) form.setValue('address_neighborhood', data.address_neighborhood);
      if (data.state != null) form.setValue('state', data.state);
      if (data.postal_code != null) form.setValue('postal_code', data.postal_code);
      if (data.phone_number != null) form.setValue('phone_number', data.phone_number);
      toast({ title: 'Sucesso', description: 'Dados do CNPJ preenchidos automaticamente.' });
    } catch (e: any) {
      const msg = e?.message || e?.response?.data?.error || 'Erro ao buscar dados do CNPJ';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setLoadingCnpj(false);
    }
  };

  return (
    <div className="bg-white p-6 border border-gray-200 rounded-lg">
      <h3 className="text-lg font-montserrat font-semibold text-primary-700 mb-4 flex items-center">
        <div className="w-2 h-2 bg-primary-500 rounded-full mr-2" />
        Informações Básicas
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="company_tax_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">CNPJ *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="12.345.678/0001-90"
                  onBlur={(e) => {
                    field.onBlur();
                    if (!isEditing) handleFetchCnpjData(e.target.value);
                  }}
                />
              </FormControl>
              {loadingCnpj && (
                <p className="text-sm text-gray-500">Buscando dados do CNPJ...</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="legal_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Razão Social *</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="Empresa Ltda"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="trade_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Nome Fantasia</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="Nome comercial"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Email</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="email"
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="contato@empresa.com"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
